import React, { useState, useEffect } from 'react';
import { heroCosts, maxTeamSize } from '../game/gameData';

const Button = ({ onClick, children, disabled }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    style={{
      backgroundColor: disabled ? 'gray' : '#91FFA4',
      color: 'black',
      padding: '8px 8px', // Reduced size
      border: 'none',
      borderRadius: '5px',
      cursor: disabled ? 'not-allowed' : 'pointer',
      transition: 'background-color 0.3s',
      fontFamily: "'Atma', sans-serif",
      fontWeight: 600,
      fontSize: '12px', // Reduced size
    }}
    onMouseOver={(e) => !disabled && (e.target.style.backgroundColor = '#FFFC91')}
    onMouseOut={(e) => !disabled && (e.target.style.backgroundColor = '#D6FF91')}
  >
    {children}
  </button>
);

const HeroIcon = ({ team, isActive }) => (
  <div
    style={{
      width: '20px',
      height: '20px',
      borderRadius: '50%',
      backgroundColor: isActive ? (team === 'My Team' ? '#5490FD' : '#FD685A') : 'gray',
      margin: '0 5px',
    }}
  />
);

const TeamStatus = ({ team, heroes }) => (
  <div style={{ display: 'flex', alignItems: 'center', margin: '0 10px' }}>
    <span style={{
      color: '#FFFFFF',
      marginRight: '10px',
      fontWeight: 600,
      fontSize: '18px',
      textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
      fontFamily: "'Atma', sans-serif",
    }}>
      {team}:
    </span>
    {heroes.map((hero, index) => (
      <HeroIcon key={index} team={team} isActive={hero.active} />
    ))}
  </div>
);

const HUD = ({ heroHeroes, enemyHeroes, currentWave }) => (
  <div style={{
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    padding: '3px',
    backgroundColor: 'rgba(37, 44, 209, 0.68)',
    color: '#AAFFAC',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontFamily: "'Atma', sans-serif",
    fontWeight: 600,
    fontSize: window.innerWidth < 768 ? '16px' : '20px',
    height: '30px',
    border: '2px solid black',
    textShadow: '2px 2px 4px rgba(0,0,0,0.7)',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.5)',
  }}>
    <TeamStatus team="My Team" heroes={heroHeroes} />
    <div style={{ fontWeight: 600, fontFamily: "'Atma', sans-serif" }}>Wave: {currentWave}</div>
    <TeamStatus team="Opponent" heroes={enemyHeroes} />
  </div>
);

const ResultsSection = ({ wins, losses, round }) => {
  const maxWins = 7;
  const maxLosses = 7;

  return (
    <div style={{
      marginBottom: '10px',
      border: '1px solid rgba(255, 255, 255, 0.5)', // Semi-opaque border
      padding: '10px',
      borderRadius: '5px',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3 style={{ color: '#FFAEAE', fontFamily: "'Atma', sans-serif", fontWeight: 600, fontSize: '20px', textShadow: '2px 2px 4px rgba(0,0,0,0.7)' }}>Results</h3>
        <span style={{ color: '#FFFFFF', fontFamily: "'Atma', sans-serif", fontSize: '16px', textShadow: '1px 1px 2px rgba(0,0,0,0.7)' }}>Current Round: {round}</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '5px' }}>
        <span style={{ color: '#FFFFFF', fontFamily: "'Atma', sans-serif", fontWeight: 600, fontSize: '16px', marginRight: '10px', textShadow: '2px 2px 4px rgba(0,0,0,0.7)' }}>Wins:</span>
        {[...Array(maxWins)].map((_, i) => (
          <span key={i} style={{ fontSize: '16px', opacity: i < wins ? 1 : 0.3 }}>
            🏆
          </span>
        ))}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '5px' }}>
        <span style={{ color: '#FFFFFF', fontFamily: "'Atma', sans-serif", fontWeight: 600, fontSize: '16px', marginRight: '10px', textShadow: '2px 2px 4px rgba(0,0,0,0.7)' }}>Losses:</span>
        {[...Array(maxLosses)].map((_, i) => (
          <span key={i} style={{ fontSize: '20px', opacity: i < losses ? 1 : 0.3 }}>
            💀
          </span>
        ))}
      </div>
    </div>
  );
};

const RecruitingSection = ({ onBuy, gold }) => (
  <div style={{
    marginBottom: '5px',
    border: '1px solid rgba(255, 255, 255, 0.5)', // Semi-opaque border
    padding: '10px',
    borderRadius: '5px',
    height: '150px', // Fixed size
  }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <h3 style={{ color: '#FFAEAE', fontFamily: "'Atma', sans-serif", fontWeight: 600, fontSize: '18px', textShadow: '2px 2px 4px rgba(0,0,0,0.7)' }}>Recruit Heroes</h3>
      <span style={{ color: '#FFCA6C', fontFamily: "'Atma', sans-serif", fontWeight: 600, fontSize: '16px', textShadow: '1px 1px 2px rgba(0,0,0,0.7)' }}>Gold: {gold}</span>
    </div>
    <Button onClick={() => onBuy('penguin')} disabled={gold < heroCosts.penguin}>
      Buy Penguin (Cost: {heroCosts.penguin})
    </Button>
    <Button onClick={() => onBuy('crocodile')} disabled={gold < heroCosts.crocodile}>
      Buy Crocodile (Cost: {heroCosts.crocodile})
    </Button>
  </div>
);

const BenchSection = ({ bench, team, onSell, onUpgrade, onAddToTeam, onRemoveFromTeam }) => (
  <div style={{
    border: '1px solid rgba(255, 255, 255, 0.5)', // Semi-opaque border
    padding: '5px',
    borderRadius: '5px',
    height: '150px', // Fixed size to match RecruitingSection
  }}>
    <h3 style={{ color: '#FFAEAE', fontFamily: "'Atma', sans-serif", fontWeight: 600, fontSize: '18px', textShadow: '1px 1px 2px rgba(0,0,0,0.7)' }}>Bench</h3>
    {bench.map((hero, index) => (
      <div key={index} style={{ marginBottom: '5px' }}>
        <span style={{ color: '#FFFFFF', fontFamily: "'Atma', sans-serif", textShadow: '2px 2px 4px rgba(0,0,0,0.7)' }}>{hero.heroType} (Level {hero.level})</span>
        <Button onClick={() => onSell(index)}>Sell</Button>
        {bench.filter(h => h.heroType === hero.heroType).length >= 3 && (
          <Button onClick={() => onUpgrade(hero.heroType)}>Upgrade</Button>
        )}
        <Button onClick={() => onAddToTeam(index)} disabled={team.length >= maxTeamSize}>
          Add to Team
        </Button>
      </div>
    ))}
  </div>
);
const ShopMenu = ({ playerData, bench, team, gold, onBuy, onSell, onUpgrade, onAddToTeam, onRemoveFromTeam, handleStartBattle }) => (
  <div style={{
    position: 'absolute',
    top: '0',
    left: '0',
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(0,0,0,0.8)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    color: '#FFFFFF',
    fontFamily: "'Atma', sans-serif",
  }}>
    <div style={{
      backgroundColor: '#6B88D0',
      padding: '5px',
      borderRadius: '15px',
      border: '2px solid black',
      boxShadow: '0px 0px 15px rgba(0, 0, 0, 0.5)',
      width: '85%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      overflowY: 'auto',
    }}>
      <ResultsSection wins={playerData.wins} losses={playerData.losses} round={playerData.round} />
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '20px',
        marginBottom: '5px',
      }}>
        <RecruitingSection onBuy={onBuy} gold={gold} />
        <BenchSection
          bench={bench}
          team={team}
          onSell={onSell}
          onUpgrade={onUpgrade}
          onAddToTeam={onAddToTeam}
          onRemoveFromTeam={onRemoveFromTeam}
        />
      </div>
      <div style={{
        border: '1px solid rgba(255, 255, 255, 0.5)',
        padding: '10px',
        borderRadius: '5px',
      }}>
        <h3 style={{ color: '#FFAEAE', fontFamily: "'Atma', sans-serif", fontWeight: 600, fontSize: '18px', textShadow: '1px 1px 2px rgba(0,0,0,0.7)', marginTop: '10px' }}>Team</h3>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', // Adjust column width dynamically
          gap: '10px',
        }}>
          {team.map((hero, index) => (
            <div key={index} style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              padding: '5px 10px', // Added padding for spacing
              borderRadius: '5px',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
            }}>
              <span style={{
                color: '#FFFFFF',
                fontFamily: "'Atma', sans-serif",
                fontSize: '14px',
                textShadow: '1px 1px 2px rgba(0,0,0,0.7)',
                whiteSpace: 'nowrap', // Prevent text wrapping
              }}>
                {hero.heroType} <strong>Lvl {hero.level}</strong>
              </span>
              <Button onClick={() => onRemoveFromTeam(index)} style={{
                padding: '5px 10px',
                marginLeft: '10px', // Space between text and button
                flexShrink: 0, // Prevent button from shrinking
              }}>
                Remove
              </Button>
            </div>
          ))}
        </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: '10px' }}>
        <Button onClick={handleStartBattle} disabled={team.length === 0} style={{ padding: '8px 8px', fontSize: '16px', fontFamily: "'Atma', sans-serif" }}>
          START BATTLE
        </Button>
      </div>
    </div>
  </div>
);








const GameUI = ({ gameRef }) => {
  const [gamePhase, setGamePhase] = useState('battle');
  const [heroHeroes, setHeroHeroes] = useState([]);
  const [enemyHeroes, setEnemyHeroes] = useState([]);
  const [currentWave, setCurrentWave] = useState(0);
  const [playerData, setPlayerData] = useState({ wins: 0, losses: 0, round: 1, gold: 2 });
  const [bench, setBench] = useState([]);
  const [team, setTeam] = useState([]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (gameRef.current) {
        const scene = gameRef.current.scene.getScene('RescueRobotron');
        if (scene) {
          setGamePhase(scene.gamePhase);
          setHeroHeroes(scene.redHeroes.getChildren().map(hero => ({ active: hero.active })));
          setEnemyHeroes(scene.blueHeroes.getChildren().map(hero => ({ active: hero.active })));
          setCurrentWave(scene.currentWave);
          setPlayerData(scene.playerData);
        }
      }
    }, 100);

    return () => clearInterval(interval);
  }, [gameRef]);

  const handleStartBattle = () => {
    if (gameRef.current) {
      const scene = gameRef.current.scene.getScene('RescueRobotron');
      scene.playerData.team = team;
      scene.enterBattlePhase();
    }
  };

  const handleBuy = (heroType) => {
    if (gameRef.current) {
      const scene = gameRef.current.scene.getScene('RescueRobotron');
      if (playerData.gold >= heroCosts[heroType]) {
        setBench([...bench, { heroType: heroType, level: 1 }]);
        scene.playerData.gold -= heroCosts[heroType];
      }
    }
  };

  const handleSell = (index) => {
    const hero = bench[index];
    if (gameRef.current) {
      const scene = gameRef.current.scene.getScene('RescueRobotron');
      setBench(bench.filter((_, i) => i !== index));
      scene.playerData.gold += heroCosts[hero.heroType];
    }
  };

  const handleUpgrade = (heroType) => {
    const upgradedHero = { heroType: heroType, level: bench.find(h => h.heroType === heroType).level + 1 };
    setBench([
      ...bench.filter(h => h.heroType !== heroType),
      upgradedHero
    ]);
  };

  const handleAddToTeam = (index) => {
    if (team.length < maxTeamSize) {
      const hero = bench[index];
      setTeam([...team, hero]);
      setBench(bench.filter((_, i) => i !== index));
    }
  };

  const handleRemoveFromTeam = (index) => {
    const hero = team[index];
    setBench([...bench, hero]);
    setTeam(team.filter((_, i) => i !== index));
  };

  return (
    <div style={{ width: '100%', height: '95%', position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}>
      {gamePhase === 'battle' && (
        <HUD heroHeroes={heroHeroes} enemyHeroes={enemyHeroes} currentWave={currentWave} />
      )}
      {gamePhase === 'shop' && (
        <div style={{ pointerEvents: 'auto' }}>
          <ShopMenu
            playerData={playerData}
            bench={bench}
            team={team}
            gold={playerData.gold}
            onBuy={handleBuy}
            onSell={handleSell}
            onUpgrade={handleUpgrade}
            onAddToTeam={handleAddToTeam}
            onRemoveFromTeam={handleRemoveFromTeam}
            handleStartBattle={handleStartBattle}
          />
        </div>
      )}
    </div>
  );
};

export default GameUI;
